import React, { useState, useEffect } from 'react'
import * as Icon from 'react-feather'
import { useAuth } from '../../AuthContext'

const MGT_BASE = 'https://api.schedpilot.com/wp-json/schedpilot/v1'

// Only shown when the matching scope was granted, so the list never promises
// more than the token will actually be able to do.
const PERMISSIONS = [
  { icon: Icon.Users,     text: 'Read your connected social media accounts',      scope: 'schedpilot:read'  },
  { icon: Icon.BarChart2, text: 'View analytics for your posts',                  scope: 'schedpilot:read'  },
  { icon: Icon.Send,      text: 'Create, schedule, and delete posts on your behalf', scope: 'schedpilot:write' },
  { icon: Icon.Image,     text: 'Upload and manage media files',                  scope: 'schedpilot:write' },
]

// ─── Full-page card wrapper ───────────────────────────────────────────────────

function PageCard({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      padding: '24px 16px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 32px rgba(0,0,0,0.09)',
        width: '100%',
        maxWidth: 440,
        overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      border: '3px solid #f1f5f9',
      borderTopColor: '#84cc16',
      animation: 'sp-spin 0.7s linear infinite',
      margin: '0 auto',
    }}>
      <style>{`@keyframes sp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function OAuthAuthorize() {
  const { name, picture } = useAuth()

  // Parse query params once
  const params       = new URLSearchParams(window.location.search)
  const clientId     = params.get('client_id')
  const redirectUri  = params.get('redirect_uri')
  const responseType = params.get('response_type')
  const state        = params.get('state')
  // PKCE + scope arrive from api.schedpilot.com/oauth/authorize and must be
  // handed back untouched — the token endpoint checks the verifier against them.
  const codeChallenge       = params.get('code_challenge')
  const codeChallengeMethod = params.get('code_challenge_method')
  const scope               = params.get('scope')

  const [appInfo,   setAppInfo]   = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [pageError, setPageError] = useState(null)  // non-recoverable
  const [actionErr, setActionErr] = useState(null)  // recoverable (approve failed)
  const [approving, setApproving] = useState(false)

  useEffect(() => {
    // Basic param validation
    if (!clientId || !redirectUri || responseType !== 'code') {
      setPageError('Invalid authorization request — missing or incorrect parameters.')
      setLoading(false)
      return
    }

    fetch(
      `${MGT_BASE}/oauth/app-info?client_id=${encodeURIComponent(clientId)}`
        + `&redirect_uri=${encodeURIComponent(redirectUri)}`
        + (scope ? `&scope=${encodeURIComponent(scope)}` : ''),
      { credentials: 'include' }
    )
      .then(r => r.json())
      .then(data => {
        if (data.code === 200) {
          setAppInfo(data)
        } else {
          setPageError(data.message || 'Application not found or redirect URI mismatch.')
        }
        setLoading(false)
      })
      .catch(() => {
        setPageError('Could not load application info. Please try again.')
        setLoading(false)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Approve ─────────────────────────────────────────────────────────────────

  const handleApprove = async () => {
    setApproving(true)
    setActionErr(null)
    try {
      const res = await fetch(`${MGT_BASE}/oauth/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id:             clientId,
          redirect_uri:          redirectUri,
          state,
          scope,
          code_challenge:        codeChallenge,
          code_challenge_method: codeChallengeMethod,
        }),
      })
      const data = await res.json()
      if (res.ok && data.auth_code) {
        // Redirect to the third-party app's callback with the auth code
        const cb = new URL(redirectUri)
        cb.searchParams.set('code', data.auth_code)
        if (state) cb.searchParams.set('state', state)
        window.location.href = cb.toString()
      } else {
        setActionErr(data.message || 'Authorization failed. Please try again.')
        setApproving(false)
      }
    } catch {
      setActionErr('Network error. Please try again.')
      setApproving(false)
    }
  }

  // ── Deny ─────────────────────────────────────────────────────────────────────

  const handleDeny = () => {
    try {
      const cb = new URL(redirectUri)
      cb.searchParams.set('error', 'access_denied')
      cb.searchParams.set('error_description', 'The user denied access.')
      if (state) cb.searchParams.set('state', state)
      window.location.href = cb.toString()
    } catch {
      // Fallback if redirectUri is somehow malformed
      window.location.href = 'https://schedpilot.com'
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <PageCard>
        <div style={{ padding: '64px 40px', textAlign: 'center' }}>
          <Spinner />
        </div>
      </PageCard>
    )
  }

  // ── Non-recoverable error ─────────────────────────────────────────────────────

  if (pageError) {
    return (
      <PageCard>
        <div style={{ padding: '52px 40px', textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Icon.AlertCircle size={24} color="#dc2626" />
          </div>
          <p style={{ fontWeight: 600, fontSize: '1.05rem', color: '#0f172a', marginBottom: 8 }}>
            Authorization Error
          </p>
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0, lineHeight: 1.6 }}>
            {pageError}
          </p>
        </div>
      </PageCard>
    )
  }

  // ── Consent page ─────────────────────────────────────────────────────────────

  return (
    <PageCard>
      {/* ── Header ── */}
      <div style={{ padding: '36px 40px 24px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
        {/* App → SchedPilot logos */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 22 }}>
          {/* App placeholder icon */}
          <div style={{
            width: 52, height: 52, borderRadius: 12,
            background: '#0f172a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.Code size={24} color="#84cc16" />
          </div>

          <Icon.ArrowRight size={16} color="#cbd5e1" />

          {/* SchedPilot icon */}
          <div style={{
            width: 52, height: 52, borderRadius: 12,
            border: '1.5px solid #e2e8f0', overflow: 'hidden',
          }}>
            <img
              src="https://cdn2.schedpilot.com/files/logo.png"
              alt="SchedPilot"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>

        <p style={{ fontWeight: 700, fontSize: '1.15rem', color: '#0f172a', marginBottom: 6, lineHeight: 1.3 }}>
          {appInfo.name}
        </p>
        <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
          wants to access your SchedPilot account
        </p>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '24px 40px' }}>

        {/* Logged-in-as chip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#f8fafc', borderRadius: 10,
          padding: '10px 14px', marginBottom: 24,
          border: '1px solid #f1f5f9',
        }}>
          <img
            src={picture || 'https://schedpilot.com/img/av.png'}
            alt={name}
            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1, marginBottom: 2 }}>
              Logged in as
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a' }}>
              {name || 'SchedPilot User'}
            </div>
          </div>
        </div>

        {/* Permissions list */}
        <p style={{
          fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8',
          textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12,
        }}>
          This app will be able to:
        </p>

        <div style={{ marginBottom: 24 }}>
          {/* No scopes in the response means an older API build — show everything. */}
          {PERMISSIONS.filter(p => !appInfo.scopes || appInfo.scopes.includes(p.scope))
                      .map(({ icon: Ico, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: '#f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico size={14} color="#16a34a" />
              </div>
              <span style={{ fontSize: '0.875rem', color: '#334155', lineHeight: 1.4 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Recoverable error */}
        {actionErr && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
            color: '#dc2626', fontSize: '0.85rem', padding: '10px 14px', marginBottom: 16,
          }}>
            {actionErr}
          </div>
        )}

        {/* Authorize button */}
        <button
          onClick={handleApprove}
          disabled={approving}
          style={{
            width: '100%', padding: '12px 0',
            background: approving ? '#a3e635' : '#84cc16',
            color: '#fff', border: 'none', borderRadius: 8,
            fontSize: '0.95rem', fontWeight: 600,
            cursor: approving ? 'default' : 'pointer',
            marginBottom: 10,
            transition: 'background 0.15s',
          }}
        >
          {approving ? 'Authorizing…' : `Authorize ${appInfo.name}`}
        </button>

        {/* Cancel button */}
        <button
          onClick={handleDeny}
          disabled={approving}
          style={{
            width: '100%', padding: '11px 0',
            background: 'transparent', color: '#64748b',
            border: '1px solid #e2e8f0', borderRadius: 8,
            fontSize: '0.875rem',
            cursor: approving ? 'default' : 'pointer',
          }}
        >
          Cancel
        </button>
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: '0 40px 28px', textAlign: 'center' }}>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0, lineHeight: 1.7 }}>
          Authorizing gives <strong style={{ color: '#64748b' }}>{appInfo.name}</strong> access to your
          account until you revoke it at{' '}
          <a href="https://app.schedpilot.com/api-access" style={{ color: '#64748b' }}>
            app.schedpilot.com/api-access
          </a>
        </p>
      </div>
    </PageCard>
  )
}
